import clsx from 'clsx'
import PropTypes from 'prop-types'
import React from 'react'
import { connect } from 'react-redux'
import compose from 'recompose/compose'
import ContentLinks from 'components/content-links/index'
import CommonLayout from 'components/layout/common'
import Loader from 'components/loader'
import Typo from 'constants/typography'
import { loadFAQ } from 'lib/api/prepared-faq'
import { withI18next } from 'lib/i18n/withI18next'
import StateStorage from 'lib/state-storage'
import { hasLocizeTranslation } from 'lib/utils/locize'
import { getShopInfo } from 'lib/utils/shop-lang'
import { PAGE_NAME } from 'pages/const'
import BasePage from '../base'
import CookieFaqSetting from './cookies-settings'
import ImageLinks from './image-links'
import styles from './style.scss'

class Faq extends BasePage {
  constructor(props) {
    super(props)

    this.state = {
      list: [],
      type: props.type,
      content: null,
      loading: true,
      error: false,
    }

    this.shop = StateStorage.getShop()
    this.lang = StateStorage.getLanguage()
  }

  static async getInitialProps({ store, query, req, res, isServer }) {
    if (isServer) {
      BasePage.loadInitialContentFromServer({ req, res, store })
    } else {
      BasePage.loadInitialContentFromClient(req, res, store)
    }

    return {
      type: (req && req?.params && req.params?.type) || query?.type || 'faq',
    }
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (prevState && nextProps.type !== prevState.type) {
      return { type: nextProps.type, loading: true }
    }

    return null
  }

  async componentDidUpdate(prevProps, prevState) {
    if (
      prevState.type !== this.state.type ||
      prevProps.internationalization.country !==
        this.props.internationalization.country ||
      prevProps.internationalization.language !==
        this.props.internationalization.language
    ) {
      this.getList()
    }
  }

  afterPageMount() {
    this.getList()
  }

  requestId = 0

  onPageLeave() {
    this.requestId += 1
  }

  async getList() {
    this.requestId += 1
    const { requestId } = this
    const { internationalization, type } = this.props
    this.setState({ loading: true, error: false })
    try {
      const { list, content } = await loadFAQ(
        type,
        internationalization.country,
        internationalization.language,
      )
      if (
        requestId !== this.requestId ||
        type !== this.props.type ||
        internationalization.country !==
          this.props.internationalization.country ||
        internationalization.language !==
          this.props.internationalization.language
      )
        return
      const resFaqs = [...list]
      const currentShop = getShopInfo({
        key: 'id_shop',
        value: internationalization.shop,
      })

      /* Adding a cookies-setting page to the list of FAQs at index 11
    to categorize consent FAQ lists in the same area */
      resFaqs.splice(11, 0, {
        id_faq: 'cookies-setting',
        title: 'Cookies Settings',
        link_rewrite: 'cookies-setting',
      })

      if (currentShop.offline_available) {
        resFaqs.push({ id_faq: 0, link_rewrite: 'stores', title: 'Store' })
      }

      this.setState({
        loading: false,
        list: resFaqs,
        content,
      })
    } catch (error) {
      if (
        requestId === this.requestId &&
        type === this.props.type &&
        internationalization.country ===
          this.props.internationalization.country &&
        internationalization.language ===
          this.props.internationalization.language
      )
        this.setState({ loading: false, error: true, content: null })
    }
  }

  isFaq() {
    return this.state.type === 'faq'
  }

  renderFaqList() {
    const { content, type } = this.state
    const isCookieSetting = type === 'cookies-setting'

    if (isCookieSetting) {
      return <CookieFaqSetting />
    }

    if (content && typeof content === 'string') {
      if (window.location.hash) {
        setTimeout(() => {
          if (document.getElementById(window.location.hash.substring(1))) {
            document
              .getElementById(window.location.hash.substring(1))
              .scrollIntoView()
          }
        }, 5000)
      }
      return (
        <div className="info-area">
          <div
            className={clsx('faq-content__text', Typo.body2)}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: content }}
          />
        </div>
      )
    }

    return null
  }

  renderFaqPage({ isFaq, props, state }) {
    if (state.error) {
      const isThai = props.internationalization.language === 'th'
      return (
        <div role="alert" className="faq-error">
          <p>
            {hasLocizeTranslation(
              props.t,
              'FAQ_LOAD_ERROR',
              isThai
                ? 'ไม่สามารถโหลดข้อมูลได้ กรุณาลองอีกครั้ง'
                : 'We couldn’t load this information. Please try again.',
            )}
          </p>
          <button type="button" onClick={() => this.getList()}>
            {hasLocizeTranslation(
              props.t,
              'FAQ_RETRY',
              isThai ? 'ลองอีกครั้ง' : 'Try again',
            )}
          </button>
        </div>
      )
    }
    const { list, type } = state

    if (isFaq) {
      return <ImageLinks list={list} shop={this.shop} lang={this.lang} />
    }

    return (
      <div className="faq-area">
        {!props.isPhone && (
          <div className="link-area">
            <ContentLinks
              list={list}
              shop={this.shop}
              lang={this.lang}
              selectedLink={type}
            />
          </div>
        )}
        {this.renderFaqList()}
      </div>
    )
  }

  render() {
    const { t } = this.props
    const { type } = this.state
    const isFaq = this.isFaq()
    const isTnc = type === 'terms-and-conditions'
    const isPrivatePolicy = type === 'privacy-policy'
    const bannerText = hasLocizeTranslation(
      t,
      'FAQ_BANNER',
      'New Return Policy',
    )

    return (
      <CommonLayout pageName={PAGE_NAME.faq} showAlertCookiesBanner={false}>
        <style jsx>{styles}</style>
        {!!bannerText?.trim() && ( // valid when it isn't empty string or all whitespace
          <div className={clsx('faq-banner', Typo.caption)}>{bannerText}</div>
        )}
        <div
          className={clsx('faq-container', {
            'less-padding': !isFaq && !isPrivatePolicy && !isTnc,
          })}
        >
          {this.state.loading ? (
            <div className="loader-container">
              <Loader />
            </div>
          ) : (
            this.renderFaqPage({
              isFaq,
              isTnc,
              props: this.props,
              state: this.state,
            })
          )}
        </div>
      </CommonLayout>
    )
  }
}

Faq.defaultProps = {
  isPhone: false,
}

Faq.propTypes = {
  isPhone: PropTypes.bool,
}

export { Faq }

export default compose(
  connect((state) => ({
    internationalization: state.internationalization,
    isPhone: state.device.isPhone,
  })),
  withI18next(),
)(Faq)
